import React, { useMemo, useState, useRef, useEffect } from "react";
import Button from "../Buttons/Button";
import CustomCheckboxNew from "../FormFields/Custom/CustomCheckboxNew";
import CustomSelect from "../FormFields/Custom/CustomSelect";
import CustomSearch from "../FormFields/Custom/CustomSearch";
import DropdownMenu from "../Buttons/DropdownMenu";
import Drawer from "../Modal/Drawer";
import { formatNumber } from "../../utils";
import ToolTip from "../Info/ToolTip";
import { Alert, Chip, FormFieldSet, NoData, showToast } from "..";
import { Reorder } from "framer-motion";

const styles = {
  cellStyle:
    "px-4 py-2 text-left border-b-[1px] border-slate-200 text-sm text-nowrap",
  headerStyle:
    "px-4 py-4 text-left border-b-[1px] border-slate-200 text-xs text-nowrap text-md font-medium min-w-[100px]",
  cellAccStyle:
    "px-4 py-1.5 text-left border-b-[1px] border-slate-200 text-xs text-nowrap",
  headerAccStyle:
    "px-4 py-2 text-left border-b-[1px] border-slate-200 text-xs text-nowrap text-md font-regular min-w-[100px]",
  trAccStyle: "hover:bg-slate-100",

  rowStyle:
    " group hover:shadow-lg transition-all duration-100 relative hover:z-50",
  activeRowStyle:
    "bg-primary-50 hover:bg-primary-50 group transition-all duration-100",
  stickyHeader: "sticky bg-primary-50 z-20",
  stickyCell: "sticky z-10",
  fixedCellBg: "bg-white group-[.bg-primary-50]:bg-primary-50",
};

const options = [
  { label: "Showing 25 rows", value: "25" },
  { label: "Showing 50 rows", value: "50" },
  { label: "Showing 100 rows", value: "100" },
];

const calculateLeftValue = (index, showCheckbox, showSlNo, columnWidths) => {
  let offset = 0;
  if (showCheckbox) offset += 48;
  if (showSlNo) offset += 64;
  for (let i = 0; i < index; i++) {
    offset += columnWidths[i] || 100;
  }
  return offset;
};

const getColumnStyles = (column) => {
  const styles = {};
  if (column.width) styles.width = column.width;
  if (column.minWidth) styles.minWidth = column.minWidth;
  if (column.maxWidth) styles.maxWidth = column.maxWidth;
  if (column.width && !column.minWidth && !column.maxWidth) {
    styles.minWidth = column.width;
    styles.maxWidth = column.width;
  }
  return styles;
};

const useColumnWidths = (tableRef, columns) => {
  const [columnWidths, setColumnWidths] = useState([]);

  useEffect(() => {
    const updateColumnWidths = () => {
      if (tableRef.current) {
        const thElements = tableRef.current.querySelectorAll("thead th");
        const widths = [];
        let startIndex = 0;
        if (thElements[0]?.classList.contains("!min-w-12")) startIndex++;
        if (thElements[startIndex]?.classList.contains("!min-w-16"))
          startIndex++;
        for (let i = startIndex; i < thElements.length; i++) {
          widths[i - startIndex] = thElements[i].getBoundingClientRect().width;
        }
        setColumnWidths(widths);
      }
    };

    updateColumnWidths();
    const resizeObserver = new ResizeObserver(updateColumnWidths);
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }
    return () => {
      if (tableRef.current) {
        resizeObserver.unobserve(tableRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [columns]);

  return columnWidths;
};

const NewTableAdv = ({
  rows = [],
  columns = [],
  info,
  showSlNo = false,
  showCheckbox = false,
  rowClickeble = false,
  accrodian = null,
  onAccordionToggle,
  tableId = null,
  tableIdInner = Math.random().toString(36).substring(2, 9),
  serverPagination = false,
  totalCount = 0,
  page = 1,
  limit = 10,
  onPageChange,
  onLimitChange,
  fixedCol = 0,
  hideToolBar = false,
  toolbarLeft,
  toolbarRight = {
    search: true,
    filter: true,
    download: true,
    add: [],
  },
  totalText,
  totalRowStyle = "",
  totalCellStyle = "bg-gray-900 text-white font-semibold",
  tableWidth = "w-full",
  tdClassName = "",
  onSelectionChange,
  defaultSelectedRows,
  defaultCheckBox = false,
  disableSelection = false,
  singleSelection = false,
  /////////////
  serverOnSearch,
  serverSerchValue,
  noImage,
}) => {
  const [selectedRows, setSelectedRows] = useState(defaultSelectedRows ?? []);
  const [showRows, setShowRows] = useState(serverPagination ? limit : 25);
  const [currentPage, setCurrentPage] = useState(1);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    columns.map((col) => ({ ...col, visible: true }))
  );
  const [expandedRows, setExpandedRows] = useState([]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  //////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////
  // New state for settings management
  const [settingName, setSettingName] = useState("");
  const [savedSettings, setSavedSettings] = useState([]);
  const [selectedSettingId, setSelectedSettingId] = useState(null);
  const userId = 100;

  const StorageUtil = {
    // Get all table settings for a specific table and user
    getTableSettings: (tableId, userId) => {
      try {
        const key = `table_settings_${tableId}_${userId}`;
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error("Error reading from localStorage:", error);
        return [];
      }
    },

    // Save a new setting or update existing one
    saveTableSetting: (tableId, userId, settingName, columns) => {
      try {
        const key = `table_settings_${tableId}_${userId}`;
        const existingSettings = StorageUtil.getTableSettings(tableId, userId);

        // Check if setting name already exists
        const existingIndex = existingSettings.findIndex(
          (s) => s.name === settingName
        );

        const newSetting = {
          id:
            existingIndex >= 0
              ? existingSettings[existingIndex].id
              : Date.now(),
          name: settingName,
          columns: columns.map((col) => ({
            key: col.key,
            visible: col.visible,
            label: col.label,
          })),
          createdAt:
            existingIndex >= 0
              ? existingSettings[existingIndex].createdAt
              : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        let updatedSettings;
        if (existingIndex >= 0) {
          // Update existing setting
          updatedSettings = [...existingSettings];
          updatedSettings[existingIndex] = newSetting;
        } else {
          // Add new setting
          updatedSettings = [...existingSettings, newSetting];
        }

        // Save to localStorage
        localStorage.setItem(key, JSON.stringify(updatedSettings));

        return {
          success: true,
          isUpdate: existingIndex >= 0,
          settings: updatedSettings,
        };
      } catch (error) {
        console.error("Error saving to localStorage:", error);
        return { success: false, error: error.message };
      }
    },

    // Delete a specific setting
    deleteSetting: (tableId, userId, settingId) => {
      try {
        const key = `table_settings_${tableId}_${userId}`;
        const existingSettings = StorageUtil.getTableSettings(tableId, userId);
        const filteredSettings = existingSettings.filter(
          (s) => s.id !== settingId
        );

        localStorage.setItem(key, JSON.stringify(filteredSettings));
        return { success: true, settings: filteredSettings };
      } catch (error) {
        console.error("Error deleting from localStorage:", error);
        return { success: false, error: error.message };
      }
    },

    // Get a specific setting by ID
    getSettingById: (tableId, userId, settingId) => {
      try {
        const settings = StorageUtil.getTableSettings(tableId, userId);
        return settings.find((s) => s.id === settingId) || null;
      } catch (error) {
        console.error("Error getting setting by ID:", error);
        return null;
      }
    },

    // Clear all settings for a table
    clearTableSettings: (tableId, userId) => {
      try {
        const key = `table_settings_${tableId}_${userId}`;
        localStorage.removeItem(key);
        return { success: true };
      } catch (error) {
        console.error("Error clearing localStorage:", error);
        return { success: false, error: error.message };
      }
    },

    // Export settings as JSON (useful for backup)
    exportSettings: (tableId, userId) => {
      try {
        const settings = StorageUtil.getTableSettings(tableId, userId);
        return {
          success: true,
          data: {
            tableId,
            userId,
            settings,
            exportedAt: new Date().toISOString(),
          },
        };
      } catch (error) {
        console.error("Error exporting settings:", error);
        return { success: false, error: error.message };
      }
    },

    // Import settings from JSON
    importSettings: (tableId, userId, importData) => {
      try {
        if (!importData.settings || !Array.isArray(importData.settings)) {
          throw new Error("Invalid import data format");
        }

        const key = `table_settings_${tableId}_${userId}`;
        const existingSettings = StorageUtil.getTableSettings(tableId, userId);

        // Merge imported settings with existing ones, avoiding duplicates
        const mergedSettings = [...existingSettings];

        importData.settings.forEach((importedSetting) => {
          const existingIndex = mergedSettings.findIndex(
            (s) => s.name === importedSetting.name
          );

          if (existingIndex >= 0) {
            // Update existing setting
            mergedSettings[existingIndex] = {
              ...importedSetting,
              id: mergedSettings[existingIndex].id, // Keep original ID
              updatedAt: new Date().toISOString(),
            };
          } else {
            // Add new setting with new ID
            mergedSettings.push({
              ...importedSetting,
              id: Date.now() + Math.random(), // Ensure unique ID
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }
        });

        localStorage.setItem(key, JSON.stringify(mergedSettings));

        return { success: true, settings: mergedSettings };
      } catch (error) {
        console.error("Error importing settings:", error);
        return { success: false, error: error.message };
      }
    },
  };

  // Save current setting - Updated function
  const saveSetting = () => {
    if (!settingName.trim()) {
      //alert("Please enter a setting name");
      showToast(
        "Please enter a setting name",
        "error",
        3000, // duration
        "text", // variant
        "md", // size
        {
          position: "top-left",
        }
      );

      return;
    }

    const result = StorageUtil.saveTableSetting(
      tableId,
      userId,
      settingName.trim(),
      visibleColumns
    );

    if (result.success) {
      // Update local state with the new settings list
      setSavedSettings(result.settings);

      // Find and set the selected setting
      const savedSetting = result.settings.find(
        (s) => s.name === settingName.trim()
      );
      if (savedSetting) {
        setSelectedSettingId(savedSetting.id);
      }
      showToast(
        result.isUpdate
          ? "Setting updated successfully!"
          : "Setting saved successfully!",
        "success",
        3000, // duration
        "text", // variant
        "md", // size
        {
          position: "top-left",
        }
      );
      // alert(
      //   result.isUpdate
      //     ? "Setting updated successfully!"
      //     : "Setting saved successfully!"
      // );

      setSettingName("");
    } else {
      //alert(`Failed to save setting: ${result.error}`);
      showToast(
        `Failed to save setting: ${result.error}`,
        "error",
        3000, // duration
        "text", // variant
        "md", // size
        {
          position: "top-left",
        }
      );
    }
  };

  // Delete setting - Updated function
  const deleteSetting = (settingId, settingName) => {
    if (confirm(`Are you sure you want to delete "${settingName}"?`)) {
      const result = StorageUtil.deleteSetting(tableId, userId, settingId);

      if (result.success) {
        setSavedSettings(result.settings);

        // Clear selected setting if it was deleted
        if (selectedSettingId === settingId) {
          setSelectedSettingId(null);
        }

        //alert("Setting deleted successfully!");
        showToast(
          "Setting deleted successfully!",
          "success",
          3000, // duration
          "text", // variant
          "md", // size
          {
            position: "top-left",
          }
        );
      } else {
        //alert(`Failed to delete setting: ${result.error}`);
        showToast(
          `Failed to delete setting: ${result.error}`,
          "error",
          3000, // duration
          "text", // variant
          "md", // size
          {
            position: "top-left",
          }
        );
      }
    }
  };

  // Load settings when selected - Enhanced function
  const loadSetting = (setting) => {
    try {
      const orderedColumns = setting.columns
        .map((savedCol) => {
          const originalCol = columns.find((col) => col.key === savedCol.key);
          return originalCol
            ? { ...originalCol, visible: savedCol.visible }
            : null;
        })
        .filter(Boolean);

      // Add any new columns that weren't in the saved setting
      const savedKeys = setting.columns.map((col) => col.key);
      const newColumns = columns
        .filter((col) => !savedKeys.includes(col.key))
        .map((col) => ({ ...col, visible: true }));

      setVisibleColumns([...orderedColumns, ...newColumns]);
      setSelectedSettingId(setting.id);

      // Optional: Show confirmation
      // alert(`Setting "${setting.name}" loaded successfully!`);
    } catch (error) {
      console.error("Error loading setting:", error);
      //alert("Failed to load setting. Please try again.");
      showToast(
        "Failed to load setting. Please try again.",
        "error",
        3000, // duration
        "text", // variant
        "md", // size
        {
          position: "top-left",
        }
      );
    }
  };

  // Enhanced useEffect for loading saved settings
  useEffect(() => {
    try {
      const settings = StorageUtil.getTableSettings(tableId, userId);
      setSavedSettings(settings);

      // Optional: Auto-load last used setting
      const lastUsedSettingId = localStorage.getItem(
        `last_used_setting_${tableId}_${userId}`
      );
      if (lastUsedSettingId && settings.length > 0) {
        const lastUsedSetting = settings.find(
          (s) => s.id === parseInt(lastUsedSettingId)
        );
        if (lastUsedSetting) {
          loadSetting(lastUsedSetting);
        }
      }
    } catch (error) {
      console.error("Error loading saved settings:", error);
      setSavedSettings([]);
    }
  }, [tableId, userId, columns]);

  //////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////
  // Inside your NewTableAdv component (after states)
  const [accordionState, setAccordionState] = useState({
    id: null,
    type: null,
  });

  const toggleAccordion = (rowId, type) => {
    setAccordionState((prev) =>
      prev.id === rowId && prev.type === type
        ? { id: null, type: null }
        : { id: rowId, type }
    );
    if (onAccordionToggle) {
      onAccordionToggle(rowId, type);
    }
  };

  useEffect(() => {
    setVisibleColumns(columns.map((col) => ({ ...col, visible: true })));
  }, [columns]);

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns((prev) =>
      prev.map((col) =>
        col.key === columnKey ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const handleReorderColumns = (newOrder) => {
    setVisibleColumns(newOrder);
  };

  const filteredColumns = visibleColumns.filter((col) => col.visible);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (tableRef.current) {
        const tableContainer = tableRef.current.closest(".tablesec");
        if (tableContainer) {
          const hasHorizontalOverflow =
            tableContainer.scrollWidth > tableContainer.clientWidth;
          setHasOverflow(hasHorizontalOverflow);
        }
      }
    };

    // Check overflow initially and on window resize
    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    // Also check when columns change
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }

    return () => {
      window.removeEventListener("resize", checkOverflow);
      resizeObserver.disconnect();
    };
  }, [filteredColumns, visibleColumns]);

  const tableRef = useRef(null);
  const columnWidths = useColumnWidths(tableRef, columns);

  const handleCheckboxChange = (rowId, isChecked) => {
    if (disableSelection) return;
    if (singleSelection) {
      if (isChecked) {
        setSelectedRows([rowId]);
      } else {
        setSelectedRows([]);
      }
    } else {
      if (isChecked) {
        setSelectedRows([...selectedRows, rowId]);
      } else {
        setSelectedRows(selectedRows.filter((id) => id !== rowId));
      }
    }
  };

  const toggleSelectAll = () => {
    if (disableSelection || singleSelection) return;
    const visibleRowIds = paginatedData.map((row, index) => row.id || index);
    const allVisibleSelected = visibleRowIds.every((id) =>
      selectedRows.includes(id)
    );
    if (allVisibleSelected) {
      setSelectedRows(selectedRows.filter((id) => !visibleRowIds.includes(id)));
    } else {
      const newSelections = [...new Set([...selectedRows, ...visibleRowIds])];
      setSelectedRows(newSelections);
    }
  };

  const someRowsSelected = () => {
    const visibleRowIds = paginatedData.map((row, index) => row.id || index);
    const selectedVisibleRows = visibleRowIds.filter((id) =>
      selectedRows.includes(id)
    );
    return (
      selectedVisibleRows.length > 0 &&
      selectedVisibleRows.length < visibleRowIds.length
    );
  };

  const startIndex = serverPagination
    ? (page - 1) * limit
    : (currentPage - 1) * showRows;
  const endIndex = serverPagination
    ? startIndex + limit
    : startIndex + showRows;
  const paginatedData = serverPagination
    ? rows
    : rows.slice(startIndex, endIndex);
  const totalPages = Math.ceil(
    (serverPagination ? totalCount : rows.length) /
      (serverPagination ? limit : showRows)
  );

  const handleRowsPerPageChange = (value) => {
    const newRowsPerPage = Number(value);
    if (serverPagination) {
      onLimitChange?.(newRowsPerPage);
    } else {
      const newTotalPages = Math.ceil(rows.length / newRowsPerPage);
      const newPage = Math.min(
        Math.ceil((currentPage * showRows) / newRowsPerPage),
        newTotalPages
      );
      setShowRows(newRowsPerPage);
      setCurrentPage(newPage);
    }
  };

  const handlePageChange = (newPage) => {
    if (serverPagination) {
      onPageChange?.(newPage);
    } else {
      setCurrentPage(newPage);
    }
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  const rowsWithIndex = useMemo(() => {
    return rows.map((row, index) => ({
      ...row,
      __originalIndex: index + 1,
    }));
  }, [rows]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rowsWithIndex;
    return [...rowsWithIndex].sort((a, b) => {
      const column = columns.find((col) => col.key === sortConfig.key);
      const valueA = column?.valueGetter
        ? column.valueGetter(a)
        : a[sortConfig.key] ?? "";
      const valueB = column?.valueGetter
        ? column.valueGetter(b)
        : b[sortConfig.key] ?? "";
      if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [rowsWithIndex, sortConfig, columns]);

  const totals = useMemo(() => {
    const totalValues = {};
    filteredColumns.forEach((column) => {
      if (column.total) {
        const sum = paginatedData.reduce((acc, row) => {
          const value = column.valueGetter
            ? column.valueGetter(row)
            : row[column.key];
          return acc + (Number(value) || 0);
        }, 0);
        totalValues[column.key] = sum.toFixed(2);
      }
    });
    return totalValues;
  }, [paginatedData, filteredColumns]);

  const firstTotalIndex = filteredColumns.findIndex((col) => col.total);
  const lastTotalIndex = filteredColumns
    .map((col, idx) => (col.total ? idx : -1))
    .filter((idx) => idx !== -1)
    .slice(-1)[0];

  const totalLabelColspan =
    firstTotalIndex >= 0
      ? firstTotalIndex + (showCheckbox ? 1 : 0) + (showSlNo ? 1 : 0)
      : 0;

  // useEffect(() => {
  //   if (defaultSelectedRows.length > 0) {
  //     setSelectedRows(defaultSelectedRows);
  //   }
  // }, [defaultSelectedRows]);

  // useEffect(() => {
  //   if (defaultSelectedRows === undefined) {
  //     // prop not provided → let user interaction control state
  //     return;
  //   }

  //   // Only update if actually different
  //   setSelectedRows((prev) => {
  //     const isSame =
  //       prev.length === defaultSelectedRows.length &&
  //       prev.every((val, idx) => val === defaultSelectedRows[idx]);

  //     return isSame ? prev : defaultSelectedRows;
  //   });
  // }, [defaultSelectedRows]);

  useEffect(() => {
    if (defaultSelectedRows !== undefined) {
      setSelectedRows((prev) => {
        // Only update if the arrays are actually different
        if (
          JSON.stringify(prev.sort()) !==
          JSON.stringify([...defaultSelectedRows].sort())
        ) {
          return [...defaultSelectedRows];
        }
        return prev;
      });
    }
  }, [defaultSelectedRows]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows]);

  return (
    <div className={`flex-1 h-full flex flex-col gap-0 ${tableWidth}`}>
      {!hideToolBar && (
        <div className="toolbar py-2 flex justify-between items-center text-sm">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center py-2 gap-2">
              <span>
                {(serverPagination ? (page - 1) * limit : startIndex) + 1}-
                {Math.min(
                  serverPagination ? page * limit : endIndex,
                  serverPagination ? totalCount : rows.length
                )}
              </span>
              <span> of {serverPagination ? totalCount : rows.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                type="icon"
                variant="text"
                color="black"
                icon="chevron_left"
                onClick={() =>
                  handlePageChange(
                    serverPagination ? page - 1 : Math.max(currentPage - 1, 1)
                  )
                }
                disabled={serverPagination ? page === 1 : currentPage === 1}
                data-dropdown-persist="true"
              />
              <Button
                size="sm"
                icon="chevron_right"
                type="icon"
                variant="text"
                color="black"
                onClick={() =>
                  handlePageChange(
                    serverPagination
                      ? page + 1
                      : Math.min(currentPage + 1, totalPages)
                  )
                }
                disabled={
                  serverPagination
                    ? page >= Math.ceil(totalCount / limit)
                    : endIndex >= rows.length
                }
                data-dropdown-persist="true"
              />
            </div>
            <div className="w-36">
              <CustomSelect
                size="sm"
                placeholder="Select rows"
                options={options}
                selected={String(serverPagination ? limit : showRows)}
                onChange={handleRowsPerPageChange}
                data-dropdown-persist="true"
              />
            </div>
            {!disableSelection && selectedRows.length > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <div>{`${selectedRows.length} ${
                  selectedRows.length > 1 ? "items" : "item"
                } selected`}</div>
                <div>
                  <Button
                    size="xs-sm"
                    type="icon"
                    variant="text"
                    color="danger"
                    icon="close"
                    data-dropdown-persist="true"
                    onClick={() => setSelectedRows([])}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="toolbar-right flex items-center gap-1 mr-3">
            {toolbarRight?.search && (
              <div>
                {/* <CustomSearch size="sm" rounded="rounded" closeBtn /> */}
                <CustomSearch
                  size="sm"
                  //value={search}
                  value={serverSerchValue}
                  onChange={serverOnSearch}
                  keyname="search" // Add this
                  rounded="rounded"
                  closeBtn
                  shadow
                />
              </div>
            )}
            {toolbarRight?.filter && (
              <div>
                <Button
                  type="icon"
                  variant="text"
                  color="black"
                  icon="filter_alt"
                  onClick={() => setOpenFilterDrawer(true)}
                />
              </div>
            )}
            {toolbarRight?.download && (
              <div>
                <DropdownMenu
                  MenuSection={
                    <div className="flex flex-col">
                      <Button
                        title={"Option 1"}
                        variant="text"
                        color="black"
                        className="!justify-start py-1"
                      />
                      <Button
                        title={"Option 2"}
                        variant="text"
                        color="black"
                        className="!justify-start py-1"
                      />
                    </div>
                  }
                >
                  <Button
                    icon="download"
                    type="icon"
                    variant="text"
                    color="black"
                  />
                </DropdownMenu>
              </div>
            )}

            <div className="flex items-center gap-1">
              {toolbarRight?.add &&
                toolbarRight.add.map((item, key) => (
                  <div key={`toolbar-add-${key}`}>
                    {item.component ? (
                      item.component
                    ) : (
                      <Button
                        icon={item?.icon}
                        type="icon"
                        variant="text"
                        color="black"
                        onClick={item?.onClick}
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      <div className="tablesec flex-1 relative overflow-x-auto shadow-md sm:rounded-lg border-[1px] border-slate-200">
        <table ref={tableRef} className={`table-auto w-full`}>
          <thead className="bg-primary-50 sticky top-0 select-none z-[40]">
            <tr>
              {showCheckbox && (
                <th
                  className={`${
                    styles.headerStyle
                  } text-center w-12 !min-w-12 !max-w-12 ${
                    fixedCol > 0 ? "sticky bg-primary-50 z-40" : ""
                  }`}
                  style={fixedCol > 0 ? { left: 0 } : {}}
                >
                  {/* Hide select all checkbox in single selection mode or when selection is disabled */}
                  {!singleSelection && !disableSelection && (
                    <CustomCheckboxNew
                      id={`select-all-checkbox-${tableIdInner}`}
                      checked={
                        paginatedData.length > 0 &&
                        paginatedData.every((row, index) =>
                          selectedRows.includes(row.id || index)
                        )
                      }
                      indeterminate={someRowsSelected()}
                      onChange={toggleSelectAll}
                      disabled={defaultCheckBox}
                    />
                  )}
                </th>
              )}
              {showSlNo && (
                <th
                  className={`${styles.headerStyle} w-16 !min-w-16 !max-w-16 ${
                    fixedCol > 0 ? "sticky bg-primary-50 z-40" : ""
                  }`}
                  style={
                    fixedCol > 0 ? { left: showCheckbox ? "48px" : 0 } : {}
                  }
                >
                  <div className="flex items-center gap-1 justify-between flex-1">
                    Sl No
                  </div>
                </th>
              )}
              {filteredColumns.map((column, index) => (
                <th
                  key={`header-${column.key}-${index}`}
                  className={`${
                    styles.headerStyle
                  } truncate overflow-hidden whitespace-nowrap ${
                    index < fixedCol ? "sticky bg-primary-50 z-40" : ""
                  } ${column.className || ""}`}
                  style={{
                    ...getColumnStyles(column), // Use dynamic column styles
                    ...(index < fixedCol
                      ? {
                          left: `${calculateLeftValue(
                            index,
                            showCheckbox,
                            showSlNo,
                            columnWidths
                          )}px`,
                        }
                      : {}),
                  }}
                >
                  <div
                    className={`flex items-center gap-4 justify-between flex-1 ${
                      column.sortable && "cursor-pointer"
                    }`}
                    onClick={() => {
                      if (column.sortable) {
                        setSortConfig({
                          key: column.key,
                          direction:
                            sortConfig.key === column.key &&
                            sortConfig.direction === "asc"
                              ? "desc"
                              : "asc",
                        });
                      }
                    }}
                  >
                    {column.label}
                    {column.sortable && (
                      <span className={`material-symbols-outlined text-xs`}>
                        {sortConfig.key === column.key
                          ? sortConfig.direction === "asc"
                            ? "arrow_upward"
                            : "arrow_downward"
                          : "expand_all"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    filteredColumns.length +
                    (showSlNo ? 1 : 0) +
                    (showCheckbox ? 1 : 0)
                  }
                  className="text-center text-gray-500"
                >
                  <div className="flex justify-center">
                    <div className="relative">
                      <NoData noImage={noImage} />
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => {
                const rowId = row.id || index;
                const isExpanded = expandedRows.includes(rowId);
                const isSelected = selectedRows.includes(rowId);

                const dynamicRowClass = columns.some((col) => col.getRowClass)
                  ? columns.find((col) => col.getRowClass)?.getRowClass(row) ||
                    ""
                  : "";

                return (
                  <React.Fragment key={`row-fragment-${rowId}-${index}`}>
                    <tr
                      className={`
                ${rowClickeble && accrodian ? "cursor-pointer select-none" : ""}
                ${isSelected ? styles.activeRowStyle : styles.rowStyle}
                ${dynamicRowClass}
              `}
                      onClick={
                        rowClickeble && accrodian
                          ? () => toggleRow(rowId)
                          : null
                      }
                    >
                      {showCheckbox && (
                        <td
                          className={`${styles.cellStyle} text-center ${
                            fixedCol > 0
                              ? `${styles.stickyCell} ${styles.fixedCellBg} z-20`
                              : ""
                          } ${tdClassName}`}
                          style={fixedCol > 0 ? { left: 0 } : {}}
                        >
                          <div className="flex gap-2 items-center">
                            <CustomCheckboxNew
                              id={`row-checkbox-${tableId}-${index}`}
                              checked={isSelected}
                              onChange={(e) =>
                                handleCheckboxChange(rowId, e.target.checked)
                              }
                              disabled={defaultCheckBox || disableSelection}
                            />
                            {info && row[info] && row[info].trim() !== "" && (
                              <ToolTip size="sm" description={row[info]} />
                            )}
                          </div>
                        </td>
                      )}

                      {showSlNo && (
                        <td
                          className={`${styles.cellStyle} ${
                            fixedCol > 0
                              ? `${styles.stickyCell} ${styles.fixedCellBg} z-20`
                              : ""
                          } ${tdClassName}`}
                          style={
                            fixedCol > 0
                              ? { left: showCheckbox ? "48px" : 0 }
                              : {}
                          }
                        >
                          {(serverPagination
                            ? (page - 1) * limit
                            : startIndex) +
                            index +
                            1}
                        </td>
                      )}

                      {filteredColumns.map((column, colIndex) => {
                        const dynamicCellClass = column.getCellClass
                          ? column.getCellClass(row)
                          : "";
                        return (
                          <td
                            key={`cell-${column.key}-${colIndex}-${index}`}
                            className={`
                      ${styles.cellStyle}
                      whitespace-nowrap
                      ${
                        colIndex < fixedCol
                          ? `${styles.stickyCell} ${styles.fixedCellBg} z-20`
                          : ""
                      }
                      ${tdClassName} ${
                              column.className || ""
                            } ${dynamicCellClass}
                    `}
                            style={{
                              ...getColumnStyles(column),
                              ...(colIndex < fixedCol
                                ? {
                                    left: `${calculateLeftValue(
                                      colIndex,
                                      showCheckbox,
                                      showSlNo,
                                      columnWidths
                                    )}px`,
                                  }
                                : {}),
                            }}
                          >
                            <div className="flex items-center justify-between w-full">
                              <div>
                                {column.render
                                  ? column.render(row, { toggleAccordion })
                                  : column.valueGetter
                                  ? column.valueGetter(row[column.key], row)
                                  : row[column.key]}
                              </div>
                              {/* single accordion button */}
                              {accrodian && column.showAccordianBtn && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRow(rowId);
                                  }}
                                  className="flex items-center ml-2"
                                >
                                  {isExpanded ? (
                                    <span className="material-symbols-rounded text-sm leading-none text-primary-500">
                                      stat_minus_1
                                    </span>
                                  ) : (
                                    <span className="material-symbols-rounded text-sm leading-none text-success-500">
                                      keyboard_arrow_up
                                    </span>
                                  )}
                                </button>
                              )}

                              {/* NEW: Multiple accordion icons */}
                              {accrodian && column.accordionIcons && (
                                <div className="flex items-center gap-2 ml-2">
                                  {column.accordionIcons.map(
                                    (icon, iconIndex) => (
                                      <button
                                        key={`accordion-btn-${rowId}-${icon.key}-${iconIndex}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleAccordion(rowId, icon.key);
                                        }}
                                        className="flex items-center"
                                      >
                                        <span className="material-symbols-rounded text-sm leading-none text-primary-500">
                                          {accordionState[rowId]?.[icon.key]
                                            ? icon.activeIcon
                                            : icon.inactiveIcon}
                                        </span>
                                      </button>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {accordionState.id === row.id && (
                      <tr>
                        <td
                          colSpan={
                            (showCheckbox ? 1 : 0) +
                            (showSlNo ? 1 : 0) +
                            visibleColumns.length
                          }
                        >
                          {typeof accrodian === "function"
                            ? accrodian({ row, type: accordionState.type })
                            : accrodian}
                        </td>
                      </tr>
                      // {visibleColumns.map((col, colIndex) => (
                      //   <td key={colIndex} className={styles.cell}>
                      //     {col.render
                      //       ? col.render(row, { toggleAccordion })
                      //       : row[col.key]}
                      //   </td>
                      // ))}
                    )}

                    {/* Existing row-level accordion */}
                    {isExpanded && (
                      <tr key={`expanded-row-${rowId}-${index}`}>
                        <td
                          colSpan={
                            filteredColumns.length +
                            (showSlNo ? 1 : 0) +
                            (showCheckbox ? 1 : 0) +
                            (accrodian ? 1 : 0)
                          }
                          className="bg-opacity-5"
                        >
                          <div className="max-w-full">
                            {React.isValidElement(accrodian)
                              ? React.cloneElement(accrodian, { rowData: row })
                              : accrodian}
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* NEW: Multiple accordion contents */}
                    {filteredColumns.map((column) =>
                      column.accordionIcons
                        ? column.accordionIcons.map(
                            (icon, iconIndex) =>
                              accordionState[rowId]?.[icon.key] && (
                                <tr
                                  key={`accordion-row-${rowId}-${icon.key}-${iconIndex}`}
                                >
                                  <td
                                    colSpan={
                                      filteredColumns.length +
                                      (showSlNo ? 1 : 0) +
                                      (showCheckbox ? 1 : 0)
                                    }
                                    className="bg-opacity-5"
                                  >
                                    <div className="max-w-full">
                                      {typeof icon.render === "function"
                                        ? icon.render(row)
                                        : null}
                                    </div>
                                  </td>
                                </tr>
                              )
                          )
                        : null
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>

          {firstTotalIndex >= 0 && (
            <tfoot>
              <tr className={`${totalRowStyle}`}>
                {totalLabelColspan > 0 && (
                  <td
                    colSpan={totalLabelColspan}
                    className={`${styles.cellStyle} font-semibold `}
                    style={
                      fixedCol > 0 && showCheckbox
                        ? { left: 0, position: "sticky", zIndex: 25 }
                        : {}
                    }
                  >
                    {totalText}
                  </td>
                )}
                {filteredColumns.map((column, colIndex) => {
                  if (colIndex < firstTotalIndex) return null;
                  const dynamicCellClass = column.getCellClass
                    ? column.getCellClass({})
                    : "";
                  return (
                    <td
                      key={`footer-${column.key}-${colIndex}`}
                      className={`${
                        styles.cellStyle
                      } truncate overflow-hidden whitespace-nowrap text-right  ${
                        colIndex <= lastTotalIndex ? totalCellStyle : ""
                      } ${
                        colIndex < fixedCol
                          ? `${styles.stickyCell} ${styles.fixedCellBg} z-25`
                          : ""
                      } ${column.className || ""} ${dynamicCellClass}`}
                      style={{
                        ...getColumnStyles(column), // Use dynamic column styles
                        ...(colIndex < fixedCol
                          ? {
                              left: `${calculateLeftValue(
                                colIndex,
                                showCheckbox,
                                showSlNo,
                                columnWidths
                              )}px`,
                            }
                          : {}),
                      }}
                    >
                      {column.total
                        ? column.totalRender
                          ? column.totalRender(totals[column.key], totals)
                          : formatNumber(totals[column.key]) || "0.00"
                        : ""}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <Drawer
        title={"Column Management"}
        isOpen={openFilterDrawer}
        onClose={() => setOpenFilterDrawer(false)}
        width="w-full max-w-md"
        position="right"
      >
        <div className="grid grid-cols-1 gap-5">
          <div className="">
            <div className="">
              {savedSettings.length > 0 ? (
                <div className="flex gap-2 flex-wrap mb-4">
                  {savedSettings.map((setting) => (
                    <div
                      key={setting.id}
                      onClick={() => loadSetting(setting)}
                      className="cursor-pointer"
                    >
                      <Chip
                        label={setting.name}
                        onDelete={() => deleteSetting(setting.id, setting.name)}
                        color={
                          setting.id === selectedSettingId ? "primary" : "info"
                        } // selected primary
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 py-2 text-center">
                  No saved settings found
                </div>
              )}
            </div>
            <div className="flex justify-between gap-2 items-center">
              <div className="flex-1">
                <FormFieldSet
                  title="Setting Name"
                  noTitle
                  placeholder="Enter setting name"
                  size="sm"
                  value={settingName}
                  onChange={setSettingName}
                />
              </div>
              <Button
                title="Save"
                size="sm"
                onClick={saveSetting}
                disabled={!settingName.trim()}
              />
            </div>
            <div className="flex flex-col">
              <Reorder.Group
                axis="y"
                values={visibleColumns}
                onReorder={handleReorderColumns}
                className="flex flex-col"
              >
                {visibleColumns.map((column) => (
                  <Reorder.Item
                    key={`column-toggle-${column.key}`}
                    value={column}
                    className={`flex items-center justify-between border-b-[1px] border-slate-200 py-2 px-2 cursor-grab active:cursor-grabbing
                      ${column.visible ? "opacity-100" : "opacity-50"}
                      hover:bg-gray-50 transition-colors duration-150
                      `}
                  >
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined !text-sm">
                        drag_indicator
                      </span>
                      <div className="body-two">{column.label}</div>
                    </div>
                    <div>
                      <Button
                        type="icon"
                        variant="text"
                        size="xs-sm"
                        color={column.visible ? "primary" : "dark"}
                        icon={column.visible ? "visibility" : "visibility_off"}
                        onClick={() => handleToggleColumn(column.key)}
                      />
                    </div>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            </div>
            <div className="flex justify-end mt-2">
              <Button
                size="sm"
                title={"Reset"}
                variant="text"
                color="black"
                onClick={() => {
                  setVisibleColumns(
                    columns.map((column) => ({ ...column, visible: true }))
                  );
                }}
              />
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default NewTableAdv;

// import React, { useMemo, useState, useRef, useEffect } from "react";
// import Button from "../Buttons/Button";
// import CustomCheckboxNew from "../FormFields/Custom/CustomCheckboxNew";
// import CustomSelect from "../FormFields/Custom/CustomSelect";
// import CustomSearch from "../FormFields/Custom/CustomSearch";
// import DropdownMenu from "../Buttons/DropdownMenu";
// import Drawer from "../Modal/Drawer";
// import { motion, Reorder } from "framer-motion";
// import { formatNumber } from "../../utils";
// import ToolTip from "../Info/ToolTip";
// import { FormFieldSet } from "..";

// const styles = {
//   cellStyle:
//     "px-4 py-2 text-left border-b-[1px] border-slate-200 text-sm text-nowrap",
//   headerStyle:
//     "px-4 py-4 text-left border-b-[1px] border-slate-200 text-xs text-nowrap text-md font-medium min-w-[100px]",
//   cellAccStyle:
//     "px-4 py-1.5 text-left border-b-[1px] border-slate-200 text-xs text-nowrap",
//   headerAccStyle:
//     "px-4 py-2 text-left border-b-[1px] border-slate-200 text-xs text-nowrap text-md font-regular min-w-[100px]",
//   trAccStyle: "hover:bg-slate-100",
//   //rowStyle: "hover:bg-slate-100 group",
//   rowStyle: " group hover:shadow-lg transition-all duration-100",
//   activeRowStyle: "bg-primary-50 hover:bg-primary-50 group",
//   stickyHeader: "sticky bg-primary-50 z-20",
//   stickyCell: "sticky z-10",
//   fixedCellBg:
//     "bg-white group-hover:bg-slate-100 group-[.bg-primary-50]:bg-primary-50",
// };

// const options = [
//   // { label: "Showing 10 rows", value: "10" },
//   { label: "Showing 25 rows", value: "25" },
//   { label: "Showing 50 rows", value: "50" },
//   { label: "Showing 100 rows", value: "100" },
// ];

// const calculateLeftValue = (index, showCheckbox, showSlNo, columnWidths) => {
//   let offset = 0;
//   if (showCheckbox) offset += 48;
//   if (showSlNo) offset += 64;
//   for (let i = 0; i < index; i++) {
//     offset += columnWidths[i] || 100;
//   }
//   return offset;
// };

// // Dynamic column width utility function
// const getColumnStyles = (column) => {
//   const styles = {};

//   // If column has specific width properties, use them
//   if (column.width) styles.width = column.width;
//   if (column.minWidth) styles.minWidth = column.minWidth;
//   if (column.maxWidth) styles.maxWidth = column.maxWidth;

//   // If only width is provided (backward compatibility), apply to all
//   if (column.width && !column.minWidth && !column.maxWidth) {
//     styles.minWidth = column.width;
//     styles.maxWidth = column.width;
//   }

//   return styles;
// };

// const useColumnWidths = (tableRef, columns) => {
//   const [columnWidths, setColumnWidths] = useState([]);

//   useEffect(() => {
//     const updateColumnWidths = () => {
//       if (tableRef.current) {
//         const thElements = tableRef.current.querySelectorAll("thead th");
//         const widths = [];
//         let startIndex = 0;
//         if (thElements[0]?.classList.contains("!min-w-12")) startIndex++;
//         if (thElements[startIndex]?.classList.contains("!min-w-16"))
//           startIndex++;
//         for (let i = startIndex; i < thElements.length; i++) {
//           widths[i - startIndex] = thElements[i].getBoundingClientRect().width;
//         }
//         setColumnWidths(widths);
//       }
//     };

//     updateColumnWidths();
//     const resizeObserver = new ResizeObserver(updateColumnWidths);
//     if (tableRef.current) {
//       resizeObserver.observe(tableRef.current);
//     }
//     return () => {
//       if (tableRef.current) {
//         resizeObserver.unobserve(tableRef.current);
//       }
//       resizeObserver.disconnect();
//     };
//   }, [columns]);

//   return columnWidths;
// };

// const NewTableAdv = ({
//   rows = [],
//   columns = [],
//   info,
//   showSlNo = false,
//   showCheckbox = false,
//   rowClickeble = false,
//   accrodian = null,
//   tableId = Math.random().toString(36).substring(2, 9),
//   serverPagination = false,
//   totalCount = 0,
//   page = 1,
//   limit = 10,
//   onPageChange,
//   onLimitChange,
//   fixedCol = 0,
//   hideToolBar = false,
//   toolbarLeft,
//   toolbarRight = {
//     search: true,
//     filter: true,
//     download: true,
//     add: [],
//   },
//   totalText,
//   totalRowStyle = "",
//   totalCellStyle = "bg-gray-900 text-white font-semibold",
//   tableWidth = "w-full", // w-full,w-auto
//   tdClassName = "",
//   onSelectionChange, // Add this prop to notify parent of selection changes
//   defaultSelectedRows = [], // New prop for default selected rows
//   defaultCheckBox = false,
//   // New props for selection control
//   disableSelection = false, // Prop to completely disable selection
//   singleSelection = false, // Prop to enable single selection mode
// }) => {
//   const [selectedRows, setSelectedRows] = useState(defaultSelectedRows);
//   const [showRows, setShowRows] = useState(serverPagination ? limit : 25);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
//   const [visibleColumns, setVisibleColumns] = useState(
//     columns.map((col) => ({ ...col, visible: true }))
//   );
//   const [expandedRows, setExpandedRows] = useState([]);
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

//   useEffect(() => {
//     setVisibleColumns(columns.map((col) => ({ ...col, visible: true })));
//   }, [columns]);

//   const handleToggleColumn = (columnKey) => {
//     setVisibleColumns((prev) =>
//       prev.map((col) =>
//         col.key === columnKey ? { ...col, visible: !col.visible } : col
//       )
//     );
//   };

//   const handleReorderColumns = (newOrder) => {
//     setVisibleColumns(newOrder);
//   };

//   const filteredColumns = visibleColumns.filter((col) => col.visible);

//   const tableRef = useRef(null);
//   const columnWidths = useColumnWidths(tableRef, columns);

//   const handleCheckboxChange = (rowId, isChecked) => {
//     // If selection is disabled, do nothing
//     if (disableSelection) return;

//     if (singleSelection) {
//       // Single selection mode: only one row can be selected at a time
//       if (isChecked) {
//         setSelectedRows([rowId]);
//       } else {
//         setSelectedRows([]);
//       }
//     } else {
//       // Multiple selection mode (original behavior)
//       if (isChecked) {
//         setSelectedRows([...selectedRows, rowId]);
//       } else {
//         setSelectedRows(selectedRows.filter((id) => id !== rowId));
//       }
//     }
//   };

//   const toggleSelectAll = () => {
//     // If selection is disabled or in single selection mode, do nothing
//     if (disableSelection || singleSelection) return;

//     const visibleRowIds = paginatedData.map((row, index) => row.id || index);
//     const allVisibleSelected = visibleRowIds.every((id) =>
//       selectedRows.includes(id)
//     );
//     if (allVisibleSelected) {
//       setSelectedRows(selectedRows.filter((id) => !visibleRowIds.includes(id)));
//     } else {
//       const newSelections = [...new Set([...selectedRows, ...visibleRowIds])];
//       setSelectedRows(newSelections);
//     }
//   };

//   const someRowsSelected = () => {
//     const visibleRowIds = paginatedData.map((row, index) => row.id || index);
//     const selectedVisibleRows = visibleRowIds.filter((id) =>
//       selectedRows.includes(id)
//     );
//     return (
//       selectedVisibleRows.length > 0 &&
//       selectedVisibleRows.length < visibleRowIds.length
//     );
//   };

//   const startIndex = serverPagination
//     ? (page - 1) * limit
//     : (currentPage - 1) * showRows;
//   const endIndex = serverPagination
//     ? startIndex + limit
//     : startIndex + showRows;
//   const paginatedData = serverPagination
//     ? rows
//     : rows.slice(startIndex, endIndex);
//   const totalPages = Math.ceil(
//     (serverPagination ? totalCount : rows.length) /
//       (serverPagination ? limit : showRows)
//   );

//   const handleRowsPerPageChange = (value) => {
//     const newRowsPerPage = Number(value);
//     if (serverPagination) {
//       onLimitChange?.(newRowsPerPage);
//     } else {
//       const newTotalPages = Math.ceil(rows.length / newRowsPerPage);
//       const newPage = Math.min(
//         Math.ceil((currentPage * showRows) / newRowsPerPage),
//         newTotalPages
//       );
//       setShowRows(newRowsPerPage);
//       setCurrentPage(newPage);
//     }
//   };

//   const handlePageChange = (newPage) => {
//     if (serverPagination) {
//       onPageChange?.(newPage);
//     } else {
//       setCurrentPage(newPage);
//     }
//   };

//   const toggleRow = (rowId) => {
//     setExpandedRows((prev) =>
//       prev.includes(rowId)
//         ? prev.filter((id) => id !== rowId)
//         : [...prev, rowId]
//     );
//   };

//   const rowsWithIndex = useMemo(() => {
//     return rows.map((row, index) => ({
//       ...row,
//       __originalIndex: index + 1,
//     }));
//   }, [rows]);

//   const sortedRows = useMemo(() => {
//     if (!sortConfig.key) return rowsWithIndex;
//     return [...rowsWithIndex].sort((a, b) => {
//       const column = columns.find((col) => col.key === sortConfig.key);
//       const valueA = column?.valueGetter
//         ? column.valueGetter(a)
//         : a[sortConfig.key] ?? "";
//       const valueB = column?.valueGetter
//         ? column.valueGetter(b)
//         : b[sortConfig.key] ?? "";

//       if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
//       if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
//       return 0;
//     });
//   }, [rowsWithIndex, sortConfig, columns]);

//   const totals = useMemo(() => {
//     const totalValues = {};
//     filteredColumns.forEach((column) => {
//       if (column.total) {
//         const sum = paginatedData.reduce((acc, row) => {
//           const value = column.valueGetter
//             ? column.valueGetter(row)
//             : row[column.key];
//           return acc + (Number(value) || 0);
//         }, 0);
//         totalValues[column.key] = sum.toFixed(2);
//       }
//     });
//     return totalValues;
//   }, [paginatedData, filteredColumns]);

//   // Find the index of the first and last column with total: true
//   const firstTotalIndex = filteredColumns.findIndex((col) => col.total);
//   const lastTotalIndex = filteredColumns
//     .map((col, idx) => (col.total ? idx : -1))
//     .filter((idx) => idx !== -1)
//     .slice(-1)[0];

//   // Calculate colspan for the total label cell
//   const totalLabelColspan =
//     firstTotalIndex >= 0
//       ? firstTotalIndex + (showCheckbox ? 1 : 0) + (showSlNo ? 1 : 0)
//       : 0;

//   useEffect(() => {
//     if (defaultSelectedRows.length > 0) {
//       setSelectedRows(defaultSelectedRows);
//     }
//   }, [defaultSelectedRows]);

//   useEffect(() => {
//     if (onSelectionChange) {
//       onSelectionChange(selectedRows);
//     }
//   }, [selectedRows]);

//   return (
//     <div className={`flex-1 h-full flex flex-col gap-0 ${tableWidth}`}>
//       {!hideToolBar && (
//         <div className="toolbar py-2 flex justify-between items-center text-sm">
//           <div className="flex justify-between items-center gap-2">
//             <div className="flex items-center py-2 gap-2">
//               <span>
//                 {(serverPagination ? (page - 1) * limit : startIndex) + 1}-
//                 {Math.min(
//                   serverPagination ? page * limit : endIndex,
//                   serverPagination ? totalCount : rows.length
//                 )}
//               </span>
//               <span> of {serverPagination ? totalCount : rows.length}</span>
//             </div>
//             <div className="flex items-center gap-2">
//               <Button
//                 size="sm"
//                 type="icon"
//                 variant="text"
//                 color="black"
//                 icon="chevron_left"
//                 onClick={() =>
//                   handlePageChange(
//                     serverPagination ? page - 1 : Math.max(currentPage - 1, 1)
//                   )
//                 }
//                 disabled={serverPagination ? page === 1 : currentPage === 1}
//                 data-dropdown-persist="true"
//               />
//               <Button
//                 size="sm"
//                 icon="chevron_right"
//                 type="icon"
//                 variant="text"
//                 color="black"
//                 onClick={() =>
//                   handlePageChange(
//                     serverPagination
//                       ? page + 1
//                       : Math.min(currentPage + 1, totalPages)
//                   )
//                 }
//                 disabled={
//                   serverPagination
//                     ? page >= Math.ceil(totalCount / limit)
//                     : endIndex >= rows.length
//                 }
//                 data-dropdown-persist="true"
//               />
//             </div>
//             <div className="w-36">
//               <CustomSelect
//                 size="sm"
//                 placeholder="Select rows"
//                 options={options}
//                 selected={String(serverPagination ? limit : showRows)}
//                 onChange={handleRowsPerPageChange}
//                 data-dropdown-persist="true"
//               />
//             </div>
//             {!disableSelection && selectedRows.length > 0 && (
//               <div className="flex items-center gap-2 text-xs">
//                 <div>{`${selectedRows.length} ${
//                   selectedRows.length > 1 ? "items" : "item"
//                 } selected`}</div>
//                 <div>
//                   <Button
//                     size="xs-sm"
//                     type="icon"
//                     variant="text"
//                     color="danger"
//                     icon="close"
//                     data-dropdown-persist="true"
//                     onClick={() => setSelectedRows([])}
//                   />
//                 </div>
//               </div>
//             )}
//           </div>
//           <div className="toolbar-right flex items-center gap-1 mr-3">
//             {toolbarRight?.search && (
//               <div>
//                 <CustomSearch size="sm" rounded="rounded" closeBtn />
//               </div>
//             )}
//             {toolbarRight?.filter && (
//               <div>
//                 <Button
//                   type="icon"
//                   variant="text"
//                   color="black"
//                   icon="filter_alt"
//                   onClick={() => setOpenFilterDrawer(true)}
//                 />
//               </div>
//             )}
//             {toolbarRight?.download && (
//               <div>
//                 <DropdownMenu
//                   MenuSection={
//                     <div className="flex flex-col">
//                       <Button
//                         title={"Option 1"}
//                         variant="text"
//                         color="black"
//                         className="!justify-start py-1"
//                       />
//                       <Button
//                         title={"Option 2"}
//                         variant="text"
//                         color="black"
//                         className="!justify-start py-1"
//                       />
//                     </div>
//                   }
//                 >
//                   <Button
//                     icon="download"
//                     type="icon"
//                     variant="text"
//                     color="black"
//                   />
//                 </DropdownMenu>
//               </div>
//             )}

//             <div className="flex items-center gap-1">
//               {toolbarRight?.add &&
//                 toolbarRight.add.map((item, key) => (
//                   <div key={`toolbar-add-${key}`}>
//                     {item.component ? (
//                       item.component
//                     ) : (
//                       <Button
//                         icon={item?.icon}
//                         type="icon"
//                         variant="text"
//                         color="black"
//                         onClick={item?.onClick}
//                       />
//                     )}
//                   </div>
//                 ))}
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="tablesec flex-1 relative overflow-x-auto shadow-md sm:rounded-lg border-[1px] border-slate-200">
//         <table ref={tableRef} className={`table-auto w-full`}>
//           <thead className="bg-primary-50 sticky top-0 select-none z-30">
//             <tr>
//               {showCheckbox && (
//                 <th
//                   className={`${
//                     styles.headerStyle
//                   } text-center w-12 !min-w-12 !max-w-12 ${
//                     fixedCol > 0 ? "sticky bg-primary-50 z-40" : ""
//                   }`}
//                   style={fixedCol > 0 ? { left: 0 } : {}}
//                 >
//                   {/* Hide select all checkbox in single selection mode or when selection is disabled */}
//                   {!singleSelection && !disableSelection && (
//                     <CustomCheckboxNew
//                       id={`select-all-checkbox-${tableId}`}
//                       checked={
//                         paginatedData.length > 0 &&
//                         paginatedData.every((row, index) =>
//                           selectedRows.includes(row.id || index)
//                         )
//                       }
//                       indeterminate={someRowsSelected()}
//                       onChange={toggleSelectAll}
//                       disabled={defaultCheckBox}
//                     />
//                   )}
//                 </th>
//               )}
//               {showSlNo && (
//                 <th
//                   className={`${styles.headerStyle} w-16 !min-w-16 !max-w-16 ${
//                     fixedCol > 0 ? "sticky bg-primary-50 z-40" : ""
//                   }`}
//                   style={
//                     fixedCol > 0 ? { left: showCheckbox ? "48px" : 0 } : {}
//                   }
//                 >
//                   <div className="flex items-center gap-1 justify-between flex-1">
//                     Sl No
//                   </div>
//                 </th>
//               )}
//               {filteredColumns.map((column, index) => (
//                 <th
//                   key={`header-${column.key}-${index}`}
//                   className={`${
//                     styles.headerStyle
//                   } truncate overflow-hidden whitespace-nowrap ${
//                     index < fixedCol ? "sticky bg-primary-50 z-40" : ""
//                   } ${column.className || ""}`}
//                   style={{
//                     ...getColumnStyles(column), // Use dynamic column styles
//                     ...(index < fixedCol
//                       ? {
//                           left: `${calculateLeftValue(
//                             index,
//                             showCheckbox,
//                             showSlNo,
//                             columnWidths
//                           )}px`,
//                         }
//                       : {}),
//                   }}
//                 >
//                   <div
//                     className={`flex items-center gap-4 justify-between flex-1 ${
//                       column.sortable && "cursor-pointer"
//                     }`}
//                     onClick={() => {
//                       if (column.sortable) {
//                         setSortConfig({
//                           key: column.key,
//                           direction:
//                             sortConfig.key === column.key &&
//                             sortConfig.direction === "asc"
//                               ? "desc"
//                               : "asc",
//                         });
//                       }
//                     }}
//                   >
//                     {column.label}
//                     {column.sortable && (
//                       <span className={`material-symbols-outlined text-xs`}>
//                         {sortConfig.key === column.key
//                           ? sortConfig.direction === "asc"
//                             ? "arrow_upward"
//                             : "arrow_downward"
//                           : "expand_all"}
//                       </span>
//                     )}
//                   </div>
//                 </th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {/* // In your NewTableAdv component, modify the row rendering section: */}
//             {paginatedData.map((row, index) => {
//               const rowId = row.id || index;
//               const isExpanded = expandedRows.includes(rowId);
//               const isSelected = selectedRows.includes(rowId);

//               // NEW: Get dynamic row class based on row data
//               const dynamicRowClass = columns.some((col) => col.getRowClass)
//                 ? columns.find((col) => col.getRowClass)?.getRowClass(row) || ""
//                 : "";

//               return (
//                 <React.Fragment key={`row-fragment-${rowId}-${index}`}>
//                   <tr
//                     className={`
//           ${rowClickeble && accrodian ? "cursor-pointer select-none" : ""}
//           ${isSelected ? styles.activeRowStyle : styles.rowStyle}
//           ${dynamicRowClass}
//         `}
//                     onClick={
//                       rowClickeble && accrodian ? () => toggleRow(rowId) : null
//                     }
//                   >
//                     {/* Rest of your row content remains the same */}
//                     {showCheckbox && (
//                       <td
//                         className={`${styles.cellStyle} text-center ${
//                           fixedCol > 0
//                             ? `${styles.stickyCell} ${styles.fixedCellBg} z-20`
//                             : ""
//                         } ${tdClassName}`}
//                         style={fixedCol > 0 ? { left: 0 } : {}}
//                       >
//                         <div className="flex gap-2 items-center">
//                           <CustomCheckboxNew
//                             id={`row-checkbox-${tableId}-${index}`}
//                             checked={isSelected}
//                             onChange={(e) =>
//                               handleCheckboxChange(rowId, e.target.checked)
//                             }
//                             disabled={defaultCheckBox || disableSelection}
//                           />

//                           {info && row[info] && row[info].trim() !== "" && (
//                             <ToolTip size="sm" description={row[info]} />
//                           )}
//                         </div>
//                       </td>
//                     )}

//                     {/* Rest of your cell rendering code remains unchanged */}
//                     {showSlNo && (
//                       <td
//                         className={`${styles.cellStyle} ${
//                           fixedCol > 0
//                             ? `${styles.stickyCell} ${styles.fixedCellBg} z-20`
//                             : ""
//                         } ${tdClassName}`}
//                         style={
//                           fixedCol > 0
//                             ? { left: showCheckbox ? "48px" : 0 }
//                             : {}
//                         }
//                       >
//                         {(serverPagination ? (page - 1) * limit : startIndex) +
//                           index +
//                           1}
//                       </td>
//                     )}

//                     {filteredColumns.map((column, colIndex) => {
//                       // Get dynamic cell class if column has a getCellClass function
//                       const dynamicCellClass = column.getCellClass
//                         ? column.getCellClass(row)
//                         : "";

//                       return (
//                         <td
//                           key={`cell-${column.key}-${colIndex}-${index}`}
//                           className={`${
//                             styles.cellStyle
//                           } truncate overflow-hidden whitespace-nowrap ${
//                             colIndex < fixedCol
//                               ? `${styles.stickyCell} ${styles.fixedCellBg} z-20`
//                               : ""
//                           } ${tdClassName} ${
//                             column.className || ""
//                           } ${dynamicCellClass}`}
//                           style={{
//                             ...getColumnStyles(column),
//                             ...(colIndex < fixedCol
//                               ? {
//                                   left: `${calculateLeftValue(
//                                     colIndex,
//                                     showCheckbox,
//                                     showSlNo,
//                                     columnWidths
//                                   )}px`,
//                                 }
//                               : {}),
//                           }}
//                         >
//                           <div className="flex items-center justify-between w-full">
//                             <div>
//                               {column.render
//                                 ? column.render(row)
//                                 : column.valueGetter
//                                 ? column.valueGetter(row[column.key], row)
//                                 : row[column.key]}
//                             </div>
//                             {accrodian && column.showAccordianBtn && (
//                               <button
//                                 onClick={(e) => {
//                                   e.stopPropagation();
//                                   toggleRow(rowId);
//                                 }}
//                                 className="flex items-center ml-2"
//                               >
//                                 {isExpanded ? (
//                                   <span className="material-symbols-rounded text-sm leading-none text-primary-500">
//                                     stat_minus_1
//                                   </span>
//                                 ) : (
//                                   <span className="material-symbols-rounded text-sm leading-none text-success-500">
//                                     keyboard_arrow_up
//                                   </span>
//                                 )}
//                               </button>
//                             )}
//                           </div>
//                         </td>
//                       );
//                     })}
//                   </tr>
//                   {isExpanded && (
//                     <tr key={`expanded-row-${rowId}-${index}`}>
//                       <td
//                         colSpan={
//                           filteredColumns.length +
//                           (showSlNo ? 1 : 0) +
//                           (showCheckbox ? 1 : 0) +
//                           (accrodian ? 1 : 0)
//                         }
//                         className="bg-opacity-5"
//                       >
//                         <div className="max-w-full">
//                           {React.isValidElement(accrodian)
//                             ? React.cloneElement(accrodian, { rowData: row })
//                             : accrodian}
//                         </div>
//                       </td>
//                     </tr>
//                   )}
//                 </React.Fragment>
//               );
//             })}
//           </tbody>

//           {firstTotalIndex >= 0 && (
//             <tfoot>
//               <tr className={`${totalRowStyle}`}>
//                 {totalLabelColspan > 0 && (
//                   <td
//                     colSpan={totalLabelColspan}
//                     className={`${styles.cellStyle} font-semibold text-right`}
//                     style={
//                       fixedCol > 0 && showCheckbox
//                         ? { left: 0, position: "sticky", zIndex: 25 }
//                         : {}
//                     }
//                   >
//                     {totalText}
//                   </td>
//                 )}
//                 {filteredColumns.map((column, colIndex) => {
//                   if (colIndex < firstTotalIndex) return null;
//                   const dynamicCellClass = column.getCellClass
//                     ? column.getCellClass({})
//                     : "";
//                   return (
//                     <td
//                       key={`footer-${column.key}-${colIndex}`}
//                       className={`${
//                         styles.cellStyle
//                       } truncate overflow-hidden whitespace-nowrap text-right ${
//                         colIndex <= lastTotalIndex ? totalCellStyle : ""
//                       } ${
//                         colIndex < fixedCol
//                           ? `${styles.stickyCell} ${styles.fixedCellBg} z-25`
//                           : ""
//                       } ${column.className || ""} ${dynamicCellClass}`}
//                       style={{
//                         ...getColumnStyles(column), // Use dynamic column styles
//                         ...(colIndex < fixedCol
//                           ? {
//                               left: `${calculateLeftValue(
//                                 colIndex,
//                                 showCheckbox,
//                                 showSlNo,
//                                 columnWidths
//                               )}px`,
//                             }
//                           : {}),
//                       }}
//                     >
//                       {column.total
//                         ? column.totalRender
//                           ? column.totalRender(totals[column.key], totals)
//                           : formatNumber(totals[column.key]) || "0.00"
//                         : ""}
//                     </td>
//                   );
//                 })}
//               </tr>
//             </tfoot>
//           )}
//         </table>
//       </div>
//       <Drawer
//         title={"Column Management"}
//         isOpen={openFilterDrawer}
//         onClose={() => setOpenFilterDrawer(false)}
//         width="w-full max-w-md"
//         position="right"
//       >
//         <div className="grid grid-cols-1 gap-5">
//           <div className="">
//             <div>show here the setting names with on click option</div>
//             <div className="flex justify-between gap-2 items-center">
//               <div className="flex-1">
//                 <FormFieldSet
//                   title="Setting Name"
//                   noTitle
//                   placeholder="Setting Name"
//                   size="sm"
//                 />
//               </div>
//               <Button title="Save" size="sm" />
//             </div>
//             <div className="flex flex-col">
//               <Reorder.Group
//                 axis="y"
//                 values={visibleColumns}
//                 onReorder={handleReorderColumns}
//                 className="flex flex-col"
//               >
//                 {visibleColumns.map((column) => (
//                   <Reorder.Item
//                     key={`column-toggle-${column.key}`}
//                     value={column}
//                     className={`flex items-center justify-between border-b-[1px] border-slate-200 py-2 px-2 cursor-grab active:cursor-grabbing
//                       ${column.visible ? "opacity-100" : "opacity-50"}
//                       hover:bg-gray-50 transition-colors duration-150
//                       `}
//                   >
//                     <div className="flex items-center gap-2">
//                       <span class="material-symbols-outlined !text-sm">
//                         drag_indicator
//                       </span>
//                       <div className="body-two">{column.label}</div>
//                     </div>
//                     <div>
//                       <Button
//                         type="icon"
//                         variant="text"
//                         size="xs-sm"
//                         color={column.visible ? "primary" : "dark"}
//                         icon={column.visible ? "visibility" : "visibility_off"}
//                         onClick={() => handleToggleColumn(column.key)}
//                       />
//                     </div>
//                   </Reorder.Item>
//                 ))}
//               </Reorder.Group>
//             </div>
//             <div className="flex justify-end mt-2">
//               <Button
//                 size="sm"
//                 title={"Reset"}
//                 variant="text"
//                 color="black"
//                 onClick={() => {
//                   setVisibleColumns(
//                     columns.map((column) => ({ ...column, visible: true }))
//                   );
//                 }}
//               />
//             </div>
//           </div>
//         </div>
//       </Drawer>
//     </div>
//   );
// };

// export default NewTableAdv;
